import React, { useCallback, useState } from "react";
import { TurnrModalContainer, TurnrModalContent } from "../../modals/TurnrModal.jsx";
import { COLORS } from "rylak-common/src/constants";
import { ENTITY_TYPE } from "~/constants";
import { TurnrTableDropdownCell } from "../TurnrTableSmart.jsx";
import { serviceIsLoadingOrNotFetched, serviceIsLoadingOrSuccessfullyFetched, useServiceHook } from "rylak-common";
import { fetchEntitiesAll } from "~/services";
import AvatarAndItemName from "../../items/AvatarAndItemName.jsx";
import { TurnrGrid } from "../../TurnrGrid.jsx";
import { SkeletonLoaderWrapper } from "../../loading/skeletons/SkeletonLoaderWrapper.jsx";
import FormParameterTitleAndDescription from "../../entityAction/form/leafFormParameters/visual/FormParameterTitleAndDescription.jsx";
import { getOrderId } from "~/cores/orderCore";

export function getTableHeadReturnItems(props) {
    return {
        id: "orderReturn.numberOfItems",
        label: "Items",
        sortable: false,
        ...props,
    };
}

function WarehouseReturnDropdownContent({ returnItemsServiceData }) {
    const returnOrderItems = returnItemsServiceData?.data?.filter((item) => item?.orderItem);
    const reportedItems = returnItemsServiceData?.data?.filter((item) => !item?.orderItem);

    return (
        <TurnrGrid container spacing={2}>
            {returnOrderItems?.length > 0 && (
                <TurnrGrid item xs={12}>
                    <FormParameterTitleAndDescription
                        formParameter={{
                            title: "Returned items",
                            description: "The warehouse matches a return containing these items:",
                        }}
                    />
                </TurnrGrid>
            )}
            {returnOrderItems?.map((item) => {
                return (
                    <TurnrGrid item xs={12} key={item?.id}>
                        <AvatarAndItemName orderItem={item?.orderItem} />
                    </TurnrGrid>
                );
            })}
            {reportedItems?.length > 0 && (
                <TurnrGrid item xs={12}>
                    <FormParameterTitleAndDescription
                        formParameter={{
                            title: "Reported items",
                            description: "These are the items that the warehouse have reported:",
                        }}
                    />
                </TurnrGrid>
            )}
            {reportedItems?.map((reportedItem) => {
                return (
                    <TurnrGrid item xs={12} key={reportedItem?.id}>
                        <FormParameterTitleAndDescription
                            formParameter={{
                                title: reportedItem.sku ? "SKU: " + reportedItem.sku : "",
                                description: reportedItem.comment ? "Comment: " + reportedItem.comment : "",
                            }}
                        />
                    </TurnrGrid>
                );
            })}
        </TurnrGrid>
    );
}

function OrderReturnDropdownContent({ returnItemsServiceData, entityTypeIsOrderLineItems }) {
    let mainItems = returnItemsServiceData?.data;
    let bundledItemsMap = {};
    if (entityTypeIsOrderLineItems) {
        const bundledItems = mainItems?.filter((item) => item?.bundledByForeignId);
        bundledItems?.forEach((item) => {
            const bundledItems = bundledItemsMap[item?.bundledByForeignId] || [];
            bundledItems.push(item);
            bundledItemsMap[item?.bundledByForeignId] = bundledItems;
        });
        mainItems = mainItems?.filter((item) => !item?.bundledByForeignId);
    }

    return (
        <TurnrGrid container spacing={2}>
            {mainItems?.map((item) => {
                const bundledItems = bundledItemsMap[item?.foreignId];

                return (
                    <>
                        <TurnrGrid item xs={12} key={item?.id}>
                            <AvatarAndItemName orderItem={item} showQuantity={entityTypeIsOrderLineItems} />
                        </TurnrGrid>
                        {bundledItems?.map((item, index) => {
                            return (
                                <>
                                    <TurnrGrid
                                        item
                                        xs={12}
                                        key={item?.id}
                                        style={{
                                            marginLeft: "43px",
                                            position: "relative",
                                        }}
                                    >
                                        <div
                                            style={{
                                                position: "absolute",
                                                left: "-20px",
                                                top: "-5px",
                                                width: "1px",
                                                height: index === bundledItems?.length - 1 ? "41px" : "60px",
                                                background: COLORS.GRAY,
                                            }}
                                        />
                                        <div
                                            style={{
                                                position: "absolute",
                                                left: "-20px",
                                                top: "35px",
                                                width: "22px",
                                                height: "1px",
                                                background: COLORS.GRAY,
                                            }}
                                        />
                                        <AvatarAndItemName orderItem={item} showQuantity={false} small={true} />
                                    </TurnrGrid>
                                </>
                            );
                        })}
                    </>
                );
            })}
        </TurnrGrid>
    );
}

function DropdownContent({ returnItemsServiceData, entityType, order, orderReturn, shipment }) {
    const entityTypeIsOrderLineItems = entityType === ENTITY_TYPE.ORDER_LINE_ITEM;
    const DropdownContentComponent = order || orderReturn || shipment ? OrderReturnDropdownContent : WarehouseReturnDropdownContent;
    return (
        <TurnrModalContainer>
            {/*TODO Handle error*/}
            <SkeletonLoaderWrapper slim loading={serviceIsLoadingOrNotFetched(returnItemsServiceData)} loadingMinHeight={"80px"} loadingMinWidth={"200px"}>
                <TurnrModalContent
                    size={"small"}
                    style={{
                        minWidth: "200px",
                        width: "min-content",
                    }}
                >
                    <DropdownContentComponent returnItemsServiceData={returnItemsServiceData} entityTypeIsOrderLineItems={entityTypeIsOrderLineItems} />
                </TurnrModalContent>
            </SkeletonLoaderWrapper>
        </TurnrModalContainer>
    );
}

export function TableCellReturnItems({
    order,
    orderReturn,
    warehouseReport,
    shipment,
    nrOfItems = order?.totalQuantityBundlesCollapsed ||
        warehouseReport?.totalQuantityBundlesCollapsed ||
        orderReturn?.totalQuantityBundlesCollapsed ||
        shipment?.totalQuantityBundlesCollapsed ||
        order?.numberOfLineItems ||
        warehouseReport?.numberOfItems ||
        orderReturn?.numberOfItems ||
        shipment?.numberOfItems,
    appProps,
    renderAsDiv,
    ...rest
}) {
    const [open, setOpen] = useState(false);

    const entityType = orderReturn || shipment ? ENTITY_TYPE.ORDER_ITEM : warehouseReport ? ENTITY_TYPE.WAREHOUSE_RETURN_ITEM : ENTITY_TYPE.ORDER_LINE_ITEM;

    const fetchData = {
        entityType,
        orderId: getOrderId(order),
        warehouseReturnId: warehouseReport?.id,
        shipmentId: shipment?.id,
        returnId: orderReturn?.id,
    };

    const { serviceData: returnItemsServiceData, fetchServiceData: fetchReturnItemsFn } = useServiceHook({
        appProps,
        fetchData,
        fetchRequested: false,
        fetchFn: fetchEntitiesAll,
    });

    const setOpenFn = useCallback(
        (open) => {
            if (open && !serviceIsLoadingOrSuccessfullyFetched(returnItemsServiceData)) {
                fetchReturnItemsFn();
            }
            setOpen(open);
        },
        [open, returnItemsServiceData],
    );

    const noItems = !nrOfItems;

    return (
        <TurnrTableDropdownCell
            {...rest}
            open={open}
            renderAsDiv={renderAsDiv}
            disabled={noItems}
            setOpen={setOpenFn}
            placement={"bottom-right"}
            dropdownContent={<DropdownContent returnItemsServiceData={returnItemsServiceData} entityType={entityType} order={order} orderReturn={orderReturn} shipment={shipment} />}
        >
            <span
                style={{
                    visibility: noItems && "hidden",
                    fontSize: "12px",
                    color: COLORS.MID_BROWN,
                }}
            >
                {nrOfItems} {orderReturn?.numberOfItems === 1 ? "item" : "items"}
            </span>
        </TurnrTableDropdownCell>
    );
}
